import React from 'react'
import ShipmentCard from './ShipmentCard.jsx'
import { CARRIERS, formatNumber } from './carriers.js'
import { assignCarrier, parsePackageDetails } from '../../utils/carrierAssignment'
import DealLink from '../shared/DealLink.jsx'

// A fact is "shared" only when every quote in the group agrees. Anything else
// renders on the disagreeing sub-rows instead — an operator's per-quote edit
// (a moved date, changed boxes) must never vanish behind a merged header.
function shared(deals, pick) {
  const value = pick(deals[0])
  const agree = deals.every(d => pick(d) === value)
  return { agree, value: agree ? value : undefined }
}

const pkgSummaryOf = (deal) => {
  const pkg = parsePackageDetails(deal.package_details)
  return `${pkg?.boxes?.length || 1} box · ${formatNumber(pkg?.totalWeight || 0)}kg`
}

export default function CollectionGroupCard({ group, selectedId, onSelect, batchMode, batchIds, onToggleSelect }) {
  const { deals } = group

  if (deals.length === 1) {
    const s = deals[0]
    return (
      <ShipmentCard
        shipment={s}
        isSelected={selectedId === s.id}
        onClick={() => onSelect(s)}
        batchMode={batchMode}
        isChecked={batchMode && Boolean(batchIds?.has(s.id))}
        onToggleSelect={onToggleSelect}
      />
    )
  }

  // Same carrier resolution as ShipmentCard; the column already guarantees all
  // members resolved to this carrier, so the first deal is representative.
  const first = deals[0]
  const autoAssigned = assignCarrier(first)
  const savedCarrier = first.carrier?.toLowerCase()
  const carrier = CARRIERS[savedCarrier && CARRIERS[savedCarrier] ? savedCarrier : autoAssigned.carrierId]

  const sendType = shared(deals, d => d.send_type)
  const contact = shared(deals, d => d.contact_name || d.seller_email || '-')
  const hasPrinter = shared(deals, d => d.has_printer?.toLowerCase() === 'yes')
  const pkgSummary = shared(deals, pkgSummaryOf)
  // The auto-assign reason derives from package_details; when it diverges the
  // per-row box summaries already show why, so it is header-only.
  const reason = shared(deals, d => assignCarrier(d).reason)
  const collectionDate = shared(deals, d => d.collection_date)
  const isDropOff = sendType.value === 'dropoff'
  // "Deliver in person" (spec_20260805): no carrier, no label — the carrier /
  // printer / box chips are courier leftovers that survive a send-type
  // switch and must not render (operator catch 2026-08-06, COL-9ADF8F11).
  const isWarehouse = sendType.value === 'warehouse_dropoff'
  const dropoffSlot = shared(deals, d => d.dropoff_slot_start)
  const slotText = dropoffSlot.agree && dropoffSlot.value
    ? `${dropoffSlot.value.slice(0, 5)}–${String(Number(dropoffSlot.value.slice(0, 2)) + 1).padStart(2, '0')}:00`
    : 'Anytime'
  // On-Hold (spec 20260911): a consolidated pickup is flagged if any member is
  // held — the whole group must not be booked while one still needs details.
  const anyOnHold = deals.some(d => d.collection_on_hold)
  const holdReasons = deals.filter(d => d.collection_on_hold).map(d => d.collection_hold_reason).filter(Boolean).join('; ')

  return (
    <div style={{
      background: 'white',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius)',
      padding: 'var(--space-3)',
      marginBottom: 'var(--space-2)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Collection · {deals.length} quotes</div>
          <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{group.code}</div>
        </div>
        {sendType.agree && (
          <span style={{
            background: isWarehouse ? '#3BB273' : isDropOff ? 'var(--color-warning)' : 'var(--color-info)',
            color: isDropOff && !isWarehouse ? '#000' : '#fff',
            fontSize: 9,
            padding: '3px 8px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4,
            fontWeight: 600,
            lineHeight: 1,
            textAlign: 'center'
          }}>
            {isWarehouse ? 'IN PERSON' : isDropOff ? 'DROP-OFF' : 'COURIER'}
          </span>
        )}
      </div>
      {contact.agree && (
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
          {contact.value}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {anyOnHold && (
          <span className="badge badge-warning" title={holdReasons || undefined} style={{ fontSize: 10 }}>
            ⏸ On hold
          </span>
        )}
        {isWarehouse ? (
          <span className="badge" style={{ background: '#3BB273', color: 'white', fontSize: 10 }}>
            {collectionDate.agree && collectionDate.value ? `${collectionDate.value} · ` : ''}{slotText}
          </span>
        ) : (
        <>
        <span className="badge" style={{ background: carrier.color, color: 'white', fontSize: 10 }}>
          {carrier.name}
        </span>
        {hasPrinter.agree && hasPrinter.value && (
          <span className="badge" style={{ fontSize: 9 }}>Printer</span>
        )}
        {pkgSummary.agree && (
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{pkgSummary.value}</span>
        )}
        </>
        )}
      </div>
      {!isWarehouse && reason.agree && (
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
          {reason.value}
        </div>
      )}
      {collectionDate.agree && collectionDate.value && (
        <div style={{ fontSize: 11, color: 'var(--color-info)', marginTop: 4, fontWeight: 500 }}>
          📅 {collectionDate.value}
        </div>
      )}
      <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 8, paddingTop: 4 }}>
        {deals.map(s => {
          const isChecked = batchMode && Boolean(batchIds?.has(s.id))
          const isSelected = selectedId === s.id
          return (
            <div
              key={s.id}
              onClick={() => {
                if (batchMode) {
                  onToggleSelect?.(s.id)
                } else {
                  onSelect(s)
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 6,
                padding: '6px',
                borderRadius: 4,
                cursor: 'pointer',
                background: isChecked ? 'rgba(77, 20, 140, 0.08)' : isSelected ? 'rgba(59, 178, 115, 0.1)' : 'transparent'
              }}
            >
              {batchMode && (
                <input
                  type="checkbox"
                  checked={isChecked}
                  readOnly
                  style={{ accentColor: '#4D148C', cursor: 'pointer' }}
                />
              )}
              <span style={{ fontWeight: 600, fontSize: 13 }}>{s.dealname || 'Unnamed Deal'}</span>
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 'auto' }}><DealLink deal={s} /></span>
              {!sendType.agree && (
                <span style={{ fontSize: 9, fontWeight: 600 }}>
                  {s.send_type === 'warehouse_dropoff' ? 'IN PERSON' : s.send_type === 'dropoff' ? 'DROP-OFF' : 'COURIER'}
                </span>
              )}
              {!contact.agree && (
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                  {s.contact_name || s.seller_email || '-'}
                </span>
              )}
              {!hasPrinter.agree && s.has_printer?.toLowerCase() === 'yes' && (
                <span className="badge" style={{ fontSize: 9 }}>Printer</span>
              )}
              {!pkgSummary.agree && (
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{pkgSummaryOf(s)}</span>
              )}
              {!collectionDate.agree && s.collection_date && (
                <span style={{ fontSize: 11, color: 'var(--color-info)', fontWeight: 500 }}>
                  📅 {s.collection_date}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
